import { useState, useRef, useCallback } from 'react';
import { usePlanningStore } from '@/stores/planning';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { useClickOutside } from '@/hooks/useClickOutside';
import { FaseInterventieCheckbox } from '@/components/project/FaseInterventieCheckbox';

type CreateType = 'feestdag' | 'verlof' | 'vrije_taak';

export function CreateDropdown() {
  const createMode = usePlanningStore((s) => s.createMode);
  const enterCreateMode = usePlanningStore((s) => s.enterCreateMode);
  const setShowProjectSearch = usePlanningStore((s) => s.setShowProjectSearch);
  const isAdministratie = useAuthStore((s) => s.hasRole('administratie'));
  const canPlanningEdit = usePermissionsStore((s) => s.canFeature('planning_edit'));

  // Read-only viewers (e.g. projectleider_easykit) get no create affordance.
  // Administratie keeps access for feestdag/verlof creation despite no planning_edit feature.
  if (!canPlanningEdit && !isAdministratie) return null;

  const [open, setOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameInputType, setNameInputType] = useState<CreateType | null>(null);
  const [interventie, setInterventie] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setNameInputType(null);
    setNameInput('');
    setInterventie(false);
  }, []);

  useClickOutside(containerRef, close, open);

  function handleItem(type: CreateType | 'inplannen') {
    if (type === 'inplannen') {
      setShowProjectSearch(true);
      close();
      return;
    }
    if (type === 'verlof') {
      enterCreateMode('verlof');
      close();
      return;
    }
    setNameInputType(type);
    setNameInput(type === 'feestdag' ? 'Feestdag' : '');
    setInterventie(false);
  }

  function submitName() {
    if (!nameInput.trim() || !nameInputType) return;
    enterCreateMode(nameInputType, nameInput.trim(), nameInputType === 'vrije_taak' && interventie);
    close();
  }

  function cancelName() {
    setNameInputType(null);
    setNameInput('');
    setInterventie(false);
  }

  const isActive = !!createMode;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
          isActive
            ? 'bg-purple-100 border-purple-400 text-purple-700'
            : 'border-gray-300 text-td-navy hover:bg-gray-50'
        }`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span className="hidden sm:inline">Nieuw</span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-50 py-1"
          role="menu"
        >
          <button
            type="button"
            onClick={() => handleItem('feestdag')}
            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
              nameInputType === 'feestdag' ? 'bg-purple-50 text-purple-700' : 'text-td-navy hover:bg-gray-50'
            }`}
            role="menuitem"
          >
            Feestdag
          </button>
          <button
            type="button"
            onClick={() => handleItem('verlof')}
            className="w-full text-left px-4 py-2.5 text-sm text-td-navy hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            Verlof
          </button>
          {!isAdministratie && (
            <>
              <button
                type="button"
                onClick={() => handleItem('vrije_taak')}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  nameInputType === 'vrije_taak' ? 'bg-purple-50 text-purple-700' : 'text-td-navy hover:bg-gray-50'
                }`}
                role="menuitem"
              >
                Project aanmaken
              </button>
              <button
                type="button"
                onClick={() => handleItem('inplannen')}
                className="w-full text-left px-4 py-2.5 text-sm text-td-navy hover:bg-gray-50 transition-colors"
                role="menuitem"
              >
                Project inplannen
              </button>
            </>
          )}

          {nameInputType && (
            <div className="border-t border-gray-100 px-4 py-3 flex items-center gap-1.5">
              <label htmlFor="create-name-input" className="sr-only">Naam</label>
              <input
                id="create-name-input"
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitName();
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    cancelName();
                  }
                }}
                placeholder={nameInputType === 'feestdag' ? 'Feestdag' : 'Taaknaam...'}
                className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                autoFocus
              />
              <button
                type="button"
                onClick={submitName}
                disabled={!nameInput.trim()}
                className="px-2.5 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue-dark transition-colors disabled:opacity-50"
              >
                OK
              </button>
              <button
                type="button"
                onClick={cancelName}
                className="px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                Annuleren
              </button>
            </div>
          )}
          {nameInputType === 'vrije_taak' && (
            <div className="px-4 pb-3">
              <FaseInterventieCheckbox checked={interventie} onChange={setInterventie} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
